import { type AgentEvent, isAgentEvent } from './events'

/** Undefined data marks malformed JSON; it still occupies one replay cursor. */
export interface SseFrame {
  data: unknown
}

export class TurnStreamError extends Error {
  constructor(public readonly status: number) {
    super(`Turn stream failed: ${status}`)
  }
}

class StreamReadError extends Error {
  constructor(cause: unknown) {
    super('Turn stream disconnected', { cause })
  }
}

function abortReason(signal?: AbortSignal): unknown {
  if (signal && 'reason' in signal) return signal.reason
  const failure = new Error('The operation was aborted')
  failure.name = 'AbortError'
  return failure
}

/** Return true when the consumer stops at a terminal frame. */
export async function consumeSseStream(
  body: ReadableStream<Uint8Array>,
  onFrame: (frame: SseFrame) => boolean | void,
  abortSignal?: AbortSignal
): Promise<boolean> {
  if (abortSignal?.aborted) throw abortReason(abortSignal)
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let dataLines: string[] = []
  let finished = false
  let cancellation: Promise<void> | undefined
  const cancel = () => {
    cancellation ??= reader.cancel().catch(() => undefined)
  }
  abortSignal?.addEventListener('abort', cancel, { once: true })

  const drainLines = (atEnd: boolean) => {
    let boundary = buffer.search(/[\r\n]/)
    while (boundary !== -1) {
      if (buffer[boundary] === '\r' && boundary === buffer.length - 1 && !atEnd) break
      const line = buffer.slice(0, boundary)
      const separatorLength = buffer[boundary] === '\r' && buffer[boundary + 1] === '\n' ? 2 : 1
      buffer = buffer.slice(boundary + separatorLength)
      if (line === '') {
        if (dataLines.length) {
          let parsed: unknown
          try {
            parsed = JSON.parse(dataLines.join('\n'))
          } catch {
            /* Malformed data still counts as a frame. */
          }
          dataLines = []
          // Keep application exceptions outside the JSON parse catch.
          if (onFrame({ data: parsed }) === true) return true
        }
      } else if (line === 'data' || line.startsWith('data:')) {
        const value = line.slice(5)
        dataLines.push(value.startsWith(' ') ? value.slice(1) : value)
      }
      boundary = buffer.search(/[\r\n]/)
    }
    return false
  }

  try {
    for (;;) {
      let chunk: ReadableStreamReadResult<Uint8Array>
      try {
        chunk = await reader.read()
      } catch (failure) {
        if (abortSignal?.aborted) throw abortReason(abortSignal)
        throw new StreamReadError(failure)
      }
      if (abortSignal?.aborted) throw abortReason(abortSignal)
      if (chunk.done) {
        finished = true
        buffer += decoder.decode()
        return drainLines(true)
      }
      buffer += decoder.decode(chunk.value, { stream: true })
      if (drainLines(false)) return true
    }
  } finally {
    abortSignal?.removeEventListener('abort', cancel)
    if (!finished) cancel()
    await cancellation
    reader.releaseLock()
  }
}

export interface FollowTurnOptions {
  fromSeq?: number
  abortSignal?: AbortSignal
  onCursor?: (cursor: number) => void
  onReconnect?: () => void
}

function waitToReconnect(milliseconds: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) throw abortReason(signal)
    const abort = () => {
      clearTimeout(timer)
      signal?.removeEventListener('abort', abort)
      reject(abortReason(signal))
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', abort)
      resolve()
    }, milliseconds)
    signal?.addEventListener('abort', abort, { once: true })
  })
}

/** Follow one accepted turn, retrying reads from its cursor without submitting work. */
export async function followAgentTurn<Content = { text?: string }, ToolResult = Record<string, unknown>>(
  openStream: (fromSeq: number, signal?: AbortSignal) => Promise<ReadableStream<Uint8Array>>,
  onEvent: (event: AgentEvent<Content, ToolResult>) => void,
  { fromSeq = 0, abortSignal, onCursor, onReconnect }: FollowTurnOptions = {}
): Promise<void> {
  let cursor = fromSeq
  for (let attempt = 0; ; attempt += 1) {
    if (abortSignal?.aborted) throw abortReason(abortSignal)
    let interruption: unknown
    try {
      let body: ReadableStream<Uint8Array>
      try {
        body = await openStream(cursor, abortSignal)
      } catch (failure) {
        if (abortSignal?.aborted) throw abortReason(abortSignal)
        if (failure instanceof TurnStreamError) throw failure
        throw new StreamReadError(failure)
      }
      const terminal = await consumeSseStream(
        body,
        (frame) => {
          cursor += 1
          onCursor?.(cursor)
          if (!isAgentEvent<Content, ToolResult>(frame.data)) return
          onEvent(frame.data)
          return frame.data.type === 'done'
        },
        abortSignal
      )
      if (terminal) return
      interruption = new StreamReadError(undefined)
    } catch (failure) {
      if (abortSignal?.aborted) throw abortReason(abortSignal)
      if (!(failure instanceof StreamReadError) && !(failure instanceof TurnStreamError)) throw failure
      if (failure instanceof TurnStreamError && failure.status < 500 && failure.status !== 429) throw failure
      interruption = failure
    }
    if (attempt >= 5) throw interruption
    onReconnect?.()
    await waitToReconnect(Math.min(1000 * 2 ** attempt, 10_000), abortSignal)
  }
}
